import { TreeItemData } from '../components/SessionTree/SessionTree.types';
import { ExaminerFunction } from '../pages/Assessment/Assessment.types';
import { SourceSystemType } from './examSessionDto.types';

export interface ISessionItemDetails {
  id: string;
  parentId: string;
  isExpanded?: boolean;
}

export interface Score {
  id: string;
  value: number;
  isCommentRequired?: boolean;
}

export interface IScoreDetails extends Score, ISessionItemDetails {}

export interface examScoreValueCommentRequired {
  examScoreValueId: string;
  isCommentRequired?: boolean;
}

export interface CommentRequired {
  isCommentRequiredEnabled?: boolean;
  examScoreValueCommentRequiredList: examScoreValueCommentRequired[];
}

export interface Scheme {
  id: string;
  gradeRuleId: string;
  number: string;
  label: string;
  name: string;
  isCommentRequiredEnabled: boolean;
  isUsedInCriteriaSetups: boolean;
  scores: Score[];
}

export interface ISchemeDetails extends Scheme, ISessionItemDetails {
  isExamSheetConfigured: boolean;
  taskNumber: string;
}

export interface GradeRules {
  id: string;
  automaticScoring: boolean;
  noAnswer: boolean;
  content: string | null;
  schemas: Scheme[];
}

export interface IGradeRulesDetails extends GradeRules, ISessionItemDetails {
  isExamSheetConfigured: boolean;
}

export interface ICriteriaSetupDetails extends CriteriaSetup, ISessionItemDetails {
  id: string;
  gradeRuleId?: string;
  schemas?: Scheme[];
  isExamSheetConfigured: boolean;
}

export interface CriteriaSetupItem {
  id: string;
  value: number;
  isError?: boolean;
}

export interface CriteriaSetup {
  id: string;
  criteriaSetupId?: string;
  leadingSchema: CriteriaSetupItem;
  dependentSchema: CriteriaSetupItem;
  fromExamScoreValueForLeadingSchema: CriteriaSetupItem;
  toExamScoreValueForLeadingSchema: CriteriaSetupItem;
  fromExamScoreValueForDependentSchema: CriteriaSetupItem;
  toExamScoreValueForDependentSchema: CriteriaSetupItem;
}

export interface CriteriaSetupDto {
  criteriaSetupId: string | null;
  leadingSchemaId: string;
  dependentSchemaId: string;
  fromExamScoreValueForLeadingSchemaId: string;
  toExamScoreValueForLeadingSchemaId: string;
  fromExamScoreValueForDependentSchemaId: string;
  toExamScoreValueForDependentSchemaId: string;
}

export interface ScoresForLeadingSchemaItem {
  examScoreValueId: string;
  score: number;
}

export interface CriteriaSetupResponseDto {
  criteriaSetupId: string;
  leadingSchemaId: string;
  dependentSchemaId: string;
  scoresForLeadingSchema: ScoresForLeadingSchemaItem[];
  scoresForDependentSchema: ScoresForLeadingSchemaItem[];
}

export interface CriteriaSetupSchemas {
  id: string;
  name: string;
  schemas: Scheme[];
}

export interface CriteriaSetupTemplate {
  id: string;
  leadingSchemas: Scheme[];
  dependentSchemas: Scheme[];
}

export interface Task {
  id: string;
  taskNumber: string;
  gradeRules: GradeRules | null;
  criteriaSetup: CriteriaSetup | null;
}

export interface ITaskDetails extends Task, ISessionItemDetails {
  examSheetId: string;
  taskGroupId?: string;
  isExamSheetConfigured: boolean;
}

export interface TaskGroup {
  id: string;
  name: string;
  examSheetId: string;
  assessmentSession: {
    id: string;
    dateFrom: string;
    dateTo: string;
    sessionStatus: sessionStatusType;
  };
  tasks: Task[];
}

export interface ITaskGroupDetails extends TaskGroup, ISessionItemDetails {
  isExamSheetConfigured: boolean;
}

export interface ExamSheet {
  examId?: string;
  code: string;
  gradeRulesImageFilePath: string | null;
  id: string;
  name: string;
  sourceSystemId?: string;
  isConfigured: boolean;
  taskGroups: TaskGroup[];
  retentionPeriod: number | null;
}

export interface IExamSheetDetails extends ExamSheet, ISessionItemDetails {}

export interface Exam {
  id: string;
  code: string;
  name: string;
  examSheets: ExamSheet[];
}

export interface IExamDetails extends Exam, ISessionItemDetails {}

export interface IExamSession {
  id: string;
  name: string;
  year: number;
  month: number;
  type: string;
  sourceSystemName: string;
  sourceSystemType: ExamSessionSystemType | undefined;
  exams: Exam[];
  isTraining: boolean;
}

export enum ExamSessionSystemType {
  SIOEO = 1,
  SIOEPKZ = 2,
  ONE = 3,
  OKE = 4,
}

export interface IExamSessionDetails extends IExamSession, ISessionItemDetails {}

export interface ISheetTypesForExamFromExternalSystem {
  id: number;
  name: string;
  code: string;
  customNumber: string;
}

export interface ExamSheetSieoPkz {
  customNumber: string;
  examMethod?: 0;
  id: string;
  recordNumber?: number;
}

export interface QualificationSioePkz {
  description: string | undefined;
  id: string;
  name: string;
}

export interface ExamSessionSioePkz {
  dateBegin: string;
  id: string;
  name: string;
  okeExamResultSubmitDate?: null;
}

export interface dtoSioePkz {
  examSession: ExamSessionSioePkz;
  examSheets: ExamSheetSieoPkz[];
  qualifications: QualificationSioePkz[];
}

export interface ISheetTypesForExamFromExternalSystemSioepkz {
  id: number;
  customNumber: string;
}

export enum importExamSessionResult {
  success,
  importedAlready,
  failed,
}

export interface ISavedSessionImportedFromExternalSystem {
  id: string;
  month: number;
  name: string;
  year: number;
  sessionType: string;
  sourceSystemId: string;
  importExamSessionResult: importExamSessionResult;
  existingExamSheetCodes: string[];
}

export interface ISavedSessionCreatedManually {
  id: string;
  name: string;
  sessionType: string;
  year: number;
  month: number;
}

export interface IDeleteResult {
  id: boolean;
}

export enum IDeleteClosed {
  AssessmentSessionNotClosed,
  GracePeriodNotExpired,
  Success,
  Failure,
}

export interface IIdResult {
  id: string;
}

export interface IExternalSystem {
  name: string;
  id: SourceSystemType;
  domainEvents?: unknown | null;
  concurrencyStamp?: unknown | null;
}

export enum sessionStatusType {
  NEW = 1,
  OPEN = 2,
  CLOSED = 3,
  BLOCKED = 4, //TODO change to proper value after backend connection
}

export interface IDragData {
  taskGroupStatus: sessionStatusType;
  taskGroupId: string;
  examSheetId: string;
}

export interface IDragItem {
  data: TreeItemData;
  dragData: IDragData;
}

export interface ISessionItems {
  schemeScores: Scheme | null;
  criteriaSetup: CriteriaSetup | null;
  gradeRules: GradeRules | null;
  task: Task | null;
  taskGroup: TaskGroup | null;
  examSheet: ExamSheet | null;
  exam: Exam | null;
}

export interface CanExamSheetBeConfiguredResults {
  hasAtLeastOneTaskGroup: boolean;
  eachTaskHasScoreValue: boolean;
  eachGradeRuleHasAtLeastOneScoringSchema: boolean;
  eachTaskGroupHasAtLeastOneTask: boolean;
  hasDomesticCoordinator: boolean;
  hasExamSheetPages: boolean;
}

export interface examSheetValidationMessage {
  propName: keyof CanExamSheetBeConfiguredResults;
  message: string;
}

export const examSheetValidationMessages: examSheetValidationMessage[] = [
  {
    propName: 'hasAtLeastOneTaskGroup',
    message: 'Arkusz posiada przynajmniej jedną GZ',
  },
  {
    propName: 'eachTaskHasScoreValue',
    message: 'Każde zadanie ma dodany schemat oceniania',
  },
  {
    propName: 'eachGradeRuleHasAtLeastOneScoringSchema',
    message: 'Każdy schemat oceniania ma przynajmniej jedno kryterium',
  },
  {
    propName: 'eachTaskGroupHasAtLeastOneTask',
    message: 'W każdej GZ przypisane jest co najmniej 1 zadanie',
  },
  {
    propName: 'hasDomesticCoordinator',
    message: 'Egzaminator jest przypisany do funkcji KK',
  },
  {
    propName: 'hasExamSheetPages',
    message: 'Jest dodany wzorcowy arkusz',
  },
];

export interface AssessmentSession {
  hasZwo?: boolean | null;
  assessmentSessionId?: string;
  name?: string;
  examTaskGroupId?: string;
  functionsInSession?: ExaminerFunction[];
}

export interface saveCriteriaSetupErrors {
  //rules from PBI: #12104
  ExamScoringValuesAreCovering: string[]; //rule 1
  EmptySetForDependentCritera: string[]; //rule 2
  IndirectConnections: string[]; //rule 3
}

export enum ExamScoringSchemaResponse {
  Success = 0,
  MissingZero = 1,
  SheetConfigured = 2,
  Failure = 3,
}

export interface ExamSheetForStats {
  examSheetId: string;
  sheetCode: string;
}

export interface AssessmentSessionForStatsDateResponse {
  examSessionId: string;
  examSessionName: string;
  examSheets: ExamSheetForStats[];
}
